/**
 * useOrbDocTutorialsIntermediateGuardsARDocEx1CheckoutLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocTutorialsIntermediateGuardsARDocEx1Checkout
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocTutorialsIntermediateGuardsAROrder, OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutState =
  | 'pending'
  | 'paid'
  | 'shipped';

export type OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent =
  | 'INIT'
  | 'PAY'
  | 'SHIP'
  | 'NEW_ORDER'
  | 'MOVED';

type OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot = 'main';
interface OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutInternalState {
  machineState: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot, OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsIntermediateGuardsAROrder>;
}

type OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[]; renderClear?: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocTutorialsIntermediateGuardsAROrder & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocTutorialsIntermediateGuardsARDocEx1CheckoutReducer(
  state: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutInternalState,
  action: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutAction
): OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocTutorialsIntermediateGuardsAROrder>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocTutorialsIntermediateGuardsAROrder>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocTutorialsIntermediateGuardsAROrder>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutInternalState = {
  machineState: 'pending',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

export interface OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutLogicReturn {
  state: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot, OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsIntermediateGuardsAROrder>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEventPayloadMap]: (payload?: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEventPayloadMap[K]) => Promise<EventResponse> };
  config?: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutConfig;
}

export function useOrbDocTutorialsIntermediateGuardsARDocEx1CheckoutLogic(config?: Partial<OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutConfig>): OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutLogicReturn {
  const mergedConfig = config;

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-tutorials-intermediate-guards-ar-doc-ex1-checkout', orbitalName: 'OrbDocTutorialsIntermediateGuardsAR', traitName: 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocTutorialsIntermediateGuardsARDocEx1CheckoutReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['OrbDocTutorialsIntermediateGuardsAROrder'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'pending' && event === 'INIT') {
      // pending → pending (on INIT)
      _newState = 'pending';
    } else if (fromState === 'pending' && event === 'PAY' && ((+(payloadObj?.amount ?? 0) > 0))) {
      // pending → paid (on PAY)
        _fields['total'] = payloadObj?.amount;
        fields = { ...(fields ?? {}), 'total': _fields['total'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsIntermediateGuardsAR', 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsIntermediateGuardsAR', trait: 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout' } }); })({ event: "PAY" as const }));
      _newState = 'paid';
    } else if (fromState === 'paid' && event === 'INIT') {
      // paid → pending (on INIT)
      _newState = 'pending';
    } else if (fromState === 'paid' && event === 'SHIP') {
      // paid → shipped (on SHIP)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsIntermediateGuardsAR', 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsIntermediateGuardsAR', trait: 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout' } }); })({ event: "SHIP" as const }));
      _newState = 'shipped';
    } else if (fromState === 'shipped' && event === 'INIT') {
      // shipped → pending (on INIT)
      _newState = 'pending';
    } else if (fromState === 'shipped' && event === 'NEW_ORDER') {
      // shipped → pending (on NEW_ORDER)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsIntermediateGuardsAR', 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsIntermediateGuardsAR', trait: 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout' } }); })({ event: "NEW_ORDER" as const }));
      _newState = 'pending';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[]; clear: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[] } => {
    switch (fromState) {
      case 'paid':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[], clear: [] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[] };
          case 'SHIP': return { write: ['main'] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[], clear: [] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'pending':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[], clear: [] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[] };
          case 'PAY': return { write: ['main'] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[], clear: [] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'shipped':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[], clear: [] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[] };
          case 'NEW_ORDER': return { write: ['main'] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[], clear: [] as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocTutorialsIntermediateGuardsAROrder': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEventPayloadMap]: (payload?: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'PAY': (payload?: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEventPayloadMap['PAY']) => dispatch('PAY', payload),
    'SHIP': () => dispatch('SHIP'),
    'NEW_ORDER': () => dispatch('NEW_ORDER'),
    'MOVED': (payload?: OrbDocTutorialsIntermediateGuardsARDocEx1CheckoutEventPayloadMap['MOVED']) => dispatch('MOVED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocTutorialsIntermediateGuardsAR.OrbDocTutorialsIntermediateGuardsARDocEx1Checkout', ['INIT', 'PAY', 'SHIP', 'NEW_ORDER'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocTutorialsIntermediateGuardsARDocEx1Checkout', (): TraitStateSnapshot => ({
      traitName: 'OrbDocTutorialsIntermediateGuardsARDocEx1Checkout',
      currentState: internalStateRef.current.machineState,
      states: ['pending', 'paid', 'shipped'],
      events: ['INIT', 'PAY', 'SHIP', 'NEW_ORDER', 'MOVED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
